import { useMemo } from "react";
import { useTrucksQuery } from "~/shared/api/govDash/govDash.api";
import { useDriverOnShiftsInRange } from "~/shared/hooks/use-active-truck-count.hook";

export interface TruckStatus {
  truckOnLine: number;
  truckOnLinePercent: number;
  truckIdle: number;
  truckIdlePercent: number;
}

interface UseTruckStatusParams {
  districtOrganizationIds: (string | number | bigint)[];
  serviceProviderIds: (string | number | bigint)[];
}

export const useTruckStatus = (params: UseTruckStatusParams): TruckStatus => {
  // RTK Query keys the cache on the serialized args, so fresh arrays per render don't refetch.
  const { data: trucksData } = useTrucksQuery({
    districtOrganizationIds: params.districtOrganizationIds.map((id) => BigInt(id)),
    serviceProviderIds: params.serviceProviderIds.map((id) => BigInt(id)),
  });

  // Today; useDriverOnShiftsInRange widens it to startOfDay/endOfDay, so the args stay stable.
  const { activeTrucks } = useDriverOnShiftsInRange({
    districtOrganizationIds: params.districtOrganizationIds,
    serviceProviderIds: params.serviceProviderIds,
    from: new Date(),
    to: new Date(),
  });

  return useMemo(() => {
    const pct = (val: number, total: number) =>
      total > 0 ? Number(((val / total) * 100).toFixed(1)) : 0;

    const truckTotal = trucksData?.trucks?.length ?? 0;
    const truckOnLine = activeTrucks ?? 0;
    const truckIdle = Math.max(truckTotal - truckOnLine, 0);

    return {
      truckOnLine,
      truckOnLinePercent: pct(truckOnLine, truckTotal),
      truckIdle,
      truckIdlePercent: pct(truckIdle, truckTotal),
    };
  }, [trucksData, activeTrucks]);
};
